import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, CalendarDays } from "lucide-react";
import { api, errorText } from "@/lib/api";
import "./StatLeaders.css";
import "./Schedule.css";

const teamLogo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);
const todayISO = () => new Date().toISOString().slice(0, 10);
const shiftISO = (iso, delta) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + delta); return d.toISOString().slice(0, 10); };
const dayLabel = (iso) => new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const chipLabel = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return { wd: d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" }).toUpperCase(), md: d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).toUpperCase() };
};
const tipTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Athens" });
const liveLabel = (g) => {
  if (g.period && g.clock && g.clock !== "0:00") {
    const p = g.period <= 4 ? `Q${g.period}` : `${g.period - 4 > 1 ? g.period - 4 : ""}OT`;
    return `${p} ${g.clock}`;
  }
  return g.status_detail || "LIVE";
};

function GameRow({ g, onOpen }) {
  const final = g.status === "post" && g.home_pts != null;
  const live = g.status === "in";
  const scored = (final || live) && g.home_pts != null && g.away_pts != null;
  const homeWin = final && g.home_pts >= g.away_pts;
  return (
    <div className="sc-game" role="button" tabIndex={0} onClick={() => onOpen(g)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(g)} data-testid={`sc-game-${g.id}`}>
      <div className="sc-match">
        <div className={`sc-side ${final && !homeWin ? "is-win" : ""}`}>
          <img src={teamLogo(g.away_code)} alt="" loading="lazy" /><b>{g.away_code}</b>
          {scored && <strong className="sc-pts">{g.away_pts}</strong>}
        </div>
        <span className="sc-at">@</span>
        <div className={`sc-side ${final && homeWin ? "is-win" : ""}`}>
          <img src={teamLogo(g.home_code)} alt="" loading="lazy" /><b>{g.home_code}</b>
          {scored && <strong className="sc-pts">{g.home_pts}</strong>}
        </div>
      </div>
      {final ? (
        <div className="sc-highs">
          {g.winner_high && <span className="sc-high"><em>W</em> {g.winner_high.name} <b>{g.winner_high.pts}</b></span>}
          {g.loser_high && <span className="sc-high"><em>L</em> {g.loser_high.name} <b>{g.loser_high.pts}</b></span>}
        </div>
      ) : (
        <div className="sc-status">{live ? <span className="sc-live" data-testid={`sc-live-${g.id}`}>{liveLabel(g)}</span> : <span className="sc-time">{tipTime(g.date_utc)}</span>}</div>
      )}
    </div>
  );
}

function TeamMenu({ teams, records, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const sel = teams.find((t) => t.code === value);
  return (
    <div className="sc-team-dd" ref={ref}>
      <button type="button" className="sc-team-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="sc-team-menu">
        {sel ? <img src={teamLogo(sel.code)} alt="" /> : <CalendarDays size={15} />}
        <span>{sel ? sel.code : "Team Schedules"}</span>
        <ChevronDown size={14} className="sc-team-caret" />
      </button>
      {open && (
        <div className="sc-team-list" role="menu" data-testid="sc-team-list">
          <button type="button" className={`sc-team-item ${!value ? "is-active" : ""}`} onClick={() => { onChange(null); setOpen(false); }} data-testid="sc-team-all">All Teams</button>
          {teams.map((t) => (
            <button type="button" key={t.code} className={`sc-team-item ${value === t.code ? "is-active" : ""}`} onClick={() => { onChange(t.code); setOpen(false); }} data-testid={`sc-team-${t.code}`}>
              <img src={teamLogo(t.code)} alt="" /><span className="sc-team-code">{t.code}</span>
              {records?.[t.code] && <small className="sc-team-rec" data-testid={`sc-team-rec-${t.code}`}>{records[t.code]}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Schedule() {
  const [start, setStart] = useState(() => shiftISO(todayISO(), -1));
  const [selected, setSelected] = useState(() => todayISO());
  const [focused, setFocused] = useState(null);
  const [teamCode, setTeamCode] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const dateRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(`/schedule?start=${start}&days=7`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(errorText(e)));
    return () => { alive = false; };
  }, [start]);

  const hasLive = useMemo(() => (data?.days || []).some((d) => d.games.some((g) => g.status === "in")), [data]);

  useEffect(() => {
    if (!hasLive) return undefined;
    let alive = true;
    const id = setInterval(() => {
      api.get(`/schedule?start=${start}&days=7`).then((r) => alive && setData(r.data)).catch(() => {});
    }, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [hasLive, start]);

  const chips = useMemo(() => Array.from({ length: 7 }, (_, i) => shiftISO(start, i)), [start]);
  const teams = useMemo(() => {
    const map = new Map();
    (data?.days || []).forEach((d) => d.games.forEach((g) => {
      [g.away_code, g.home_code].forEach((c) => { if (c) map.set(c, { code: c }); });
    }));
    return [...map.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [data]);

  const days = useMemo(() => {
    let list = data?.days || [];
    if (focused) list = list.filter((d) => d.date === focused);
    if (teamCode) list = list.map((d) => ({ ...d, games: d.games.filter((g) => g.away_code === teamCode || g.home_code === teamCode) }));
    return list;
  }, [data, focused, teamCode]);

  const openGame = (g) => navigate(`/?day=${g.game_day}&game=${g.id}`);
  const pickDay = (iso) => { setSelected(iso); setFocused(iso); };
  const jumpTo = (iso) => { setStart(shiftISO(iso, -1)); setSelected(iso); setFocused(null); };

  return (
    <div className="sl-wrap sl-wide" data-testid="schedule-page">
      <div className="sl-top">
        <div>
          <span className="sl-overline">League</span>
          <h1 className="sl-title">NBA Schedule</h1>
        </div>
        <div className="sc-top-actions">
          <button type="button" className="sl-back" onClick={() => jumpTo(todayISO())} data-testid="sc-today">Today</button>
          <TeamMenu teams={teams} records={data?.records} value={teamCode} onChange={setTeamCode} />
        </div>
      </div>
      <div className="sc-strip" data-testid="sc-strip">
        <button type="button" className="sc-arrow" onClick={() => setStart(shiftISO(start, -7))} data-testid="sc-prev" aria-label="Previous week"><ChevronLeft size={20} /></button>
        <div className="sc-chips" data-testid="sc-chips">
          {chips.map((iso) => { const c = chipLabel(iso); return (
            <button type="button" key={iso} className={`sc-chip ${selected === iso ? "is-active" : ""} ${iso === todayISO() ? "is-today" : ""}`} onClick={() => pickDay(iso)} data-testid={`sc-chip-${iso}`}>
              <em>{c.wd}</em><b>{c.md}</b>
            </button>
          ); })}
        </div>
        <button type="button" className="sc-arrow" onClick={() => setStart(shiftISO(start, 7))} data-testid="sc-next" aria-label="Next week"><ChevronRight size={20} /></button>
        <button type="button" className="sc-cal" onClick={() => dateRef.current?.showPicker?.() ?? dateRef.current?.focus()} data-testid="sc-calendar" aria-label="Pick a date"><CalendarDays size={18} /></button>
        <input ref={dateRef} type="date" className="sc-date-input" value={selected} onChange={(e) => e.target.value && jumpTo(e.target.value)} data-testid="sc-date-input" aria-hidden="true" tabIndex={-1} />
      </div>
      {(focused || teamCode) && (
        <div className="sc-filters" data-testid="sc-filters">
          {focused && <button type="button" className="sc-pill" onClick={() => setFocused(null)} data-testid="sc-clear-day">All days ✕</button>}
          {teamCode && <button type="button" className="sc-pill" onClick={() => setTeamCode(null)} data-testid="sc-clear-team">{teamCode} only ✕</button>}
        </div>
      )}
      {error && <div className="sl-state">{error}</div>}
      {!data && !error && <div className="sl-state">Loading schedule…</div>}
      {data && days.map((day) => (
        <section className="sc-day" id={`day-${day.date}`} key={day.date} data-testid={`sc-day-${day.date}`}>
          <h2 className="sc-day-title">{dayLabel(day.date)}</h2>
          {day.games.length === 0 ? (
            <p className="sc-empty">{teamCode ? `No ${teamCode} games` : "No games scheduled"}</p>
          ) : (
            <div className="sc-grid">{day.games.map((g) => <GameRow key={g.id} g={g} onOpen={openGame} />)}</div>
          )}
        </section>
      ))}
    </div>
  );
}
